import React, {useCallback, useEffect, useState} from 'react';

import {
    platformCreateOrganization,
    platformGetOrganizations,
    platformResetOrgAdminPassword
} from '../../lib/labcode-platform-api';

import styles from '../staff-portal/staff-portal.css';

const PlatformNetworksView = () => {
    const [rows, setRows] = useState([]);
    const [loadError, setLoadError] = useState(null);
    const [loadingList, setLoadingList] = useState(true);

    const [netName, setNetName] = useState('');
    const [adminEmail, setAdminEmail] = useState('');
    const [adminFullName, setAdminFullName] = useState('');
    const [adminPassword, setAdminPassword] = useState('');
    const [formError, setFormError] = useState(null);
    const [pending, setPending] = useState(false);

    const [resetTarget, setResetTarget] = useState(null);
    const [resetPwd, setResetPwd] = useState('');
    const [resetPwd2, setResetPwd2] = useState('');
    const [resetError, setResetError] = useState(null);
    const [resetBusy, setResetBusy] = useState(false);

    const reload = useCallback(async () => {
        setLoadError(null);
        setLoadingList(true);
        try {
            const { organizations: o } = await platformGetOrganizations();
            setRows(o || []);
        } catch (e) {
            setRows([]);
            setLoadError(e && e.message ? e.message : 'Erro ao listar redes.');
        } finally {
            setLoadingList(false);
        }
    }, []);

    useEffect(() => {
        reload();
    }, [reload]);

    const handleCreate = useCallback(
        async e => {
            e.preventDefault();
            setFormError(null);
            const n = netName.trim();
            const em = adminEmail.trim().toLowerCase();
            const fn = adminFullName.trim();
            const pw = adminPassword;
            if (!n) {
                setFormError('Indique o nome da rede.');
                return;
            }
            if (!em || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) {
                setFormError('Email do responsável inválido.');
                return;
            }
            if (!fn) {
                setFormError('Indique o nome completo do responsável (gestor da rede).');
                return;
            }
            if (!pw || pw.length < 8) {
                setFormError('Palavra-passe do responsável: mínimo 8 caracteres.');
                return;
            }
            setPending(true);
            try {
                await platformCreateOrganization({
                    name: n,
                    orgAdmin: {
                        email: em,
                        fullName: fn,
                        password: pw
                    }
                });
                setNetName('');
                setAdminEmail('');
                setAdminFullName('');
                setAdminPassword('');
                await reload();
            } catch (err) {
                setFormError(err && err.message ? err.message : 'Erro ao criar rede.');
            } finally {
                setPending(false);
            }
        },
        [netName, adminEmail, adminFullName, adminPassword, reload]
    );

    const openReset = useCallback(row => {
        if (!row.orgAdmin || !row.orgAdmin.email) {
            return;
        }
        setResetError(null);
        setResetPwd('');
        setResetPwd2('');
        setResetTarget({
            orgId: row.id,
            name: row.name,
            email: row.orgAdmin.email
        });
    }, []);

    const closeReset = useCallback(() => {
        setResetTarget(null);
        setResetPwd('');
        setResetPwd2('');
        setResetError(null);
    }, []);

    const handleResetSubmit = useCallback(
        async e => {
            e.preventDefault();
            if (!resetTarget) return;
            setResetError(null);
            if (!resetPwd || resetPwd.length < 8) {
                setResetError('Nova palavra-passe: mínimo 8 caracteres.');
                return;
            }
            if (resetPwd !== resetPwd2) {
                setResetError('As palavras-passe não coincidem.');
                return;
            }
            setResetBusy(true);
            try {
                await platformResetOrgAdminPassword(resetTarget.orgId, {
                    password: resetPwd
                });
                closeReset();
            } catch (err) {
                setResetError(
                    err && err.message ? err.message : 'Erro ao actualizar palavra-passe.'
                );
            } finally {
                setResetBusy(false);
            }
        },
        [resetTarget, resetPwd, resetPwd2, closeReset]
    );

    return (
        <>
            <h2>{'Redes (organizações)'}</h2>
            <p className={styles.muted}>
                {
                    'Cada rede tem um responsável com papel «gestor da rede» (org_admin), que depois cria escolas, coordenadores e turmas no portal de equipa escolar.'
                }
            </p>
            {loadError ? (
                <p
                    className={styles.errorText}
                    role="alert"
                >
                    {loadError}
                </p>
            ) : null}
            {loadingList ? (
                <p className={styles.muted}>
                    {'A carregar…'}
                </p>
            ) : null}
            <table className={styles.dataTable}>
                <thead>
                    <tr>
                        <th>{'Nome da rede'}</th>
                        <th>{'Nome do responsável'}</th>
                        <th>{'Email do responsável'}</th>
                        <th>{'Criada'}</th>
                        <th>{'Acções'}</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.length === 0 && !loadingList ? (
                        <tr>
                            <td colSpan={5}>
                                {'Nenhuma rede registada.'}
                            </td>
                        </tr>
                    ) : (
                        rows.map(r => (
                            <tr key={r.id}>
                                <td>{r.name}</td>
                                <td>{r.orgAdmin ? r.orgAdmin.fullName : '—'}</td>
                                <td>{r.orgAdmin ? r.orgAdmin.email : '—'}</td>
                                <td>
                                    {r.createdAt ?
                                        new Date(r.createdAt).toLocaleString() :
                                        '—'}
                                </td>
                                <td>
                                    <button
                                        className={styles.btnPrimary}
                                        disabled={!r.orgAdmin}
                                        type="button"
                                        onClick={() => openReset(r)}
                                    >
                                        {'Repor palavra-passe'}
                                    </button>
                                </td>
                            </tr>
                        ))
                    )}
                </tbody>
            </table>

            {resetTarget ? (
                <div className={styles.formCard}>
                    <h3
                        className={styles.muted}
                        style={{marginTop: 0}}
                    >
                        {'Nova palavra-passe do responsável'}
                    </h3>
                    <p className={styles.muted}>
                        <strong>{resetTarget.name}</strong>
                        {' · '}
                        {resetTarget.email}
                    </p>
                    {resetError ? (
                        <p
                            className={styles.errorText}
                            role="alert"
                        >
                            {resetError}
                        </p>
                    ) : null}
                    <form onSubmit={handleResetSubmit}>
                        <div className={styles.formRow}>
                            <input
                                autoComplete="new-password"
                                className={styles.textInput}
                                maxLength={128}
                                minLength={8}
                                placeholder="Nova palavra-passe (mín. 8)"
                                type="password"
                                value={resetPwd}
                                disabled={resetBusy}
                                onChange={e => setResetPwd(e.target.value)}
                            />
                            <input
                                autoComplete="new-password"
                                className={styles.textInput}
                                maxLength={128}
                                minLength={8}
                                placeholder="Confirmar palavra-passe"
                                type="password"
                                value={resetPwd2}
                                disabled={resetBusy}
                                onChange={e => setResetPwd2(e.target.value)}
                            />
                        </div>
                        <div className={styles.formRow}>
                            <button
                                className={styles.btnPrimary}
                                disabled={resetBusy}
                                type="submit"
                            >
                                {resetBusy ? 'A guardar…' : 'Guardar nova palavra-passe'}
                            </button>
                            <button
                                className={styles.btnNeutral}
                                disabled={resetBusy}
                                type="button"
                                onClick={closeReset}
                            >
                                {'Cancelar'}
                            </button>
                        </div>
                    </form>
                </div>
            ) : null}

            <div className={styles.formCard}>
                <h3
                    className={styles.muted}
                    style={{marginTop: 0}}
                >
                    {'Nova rede + responsável'}
                </h3>
                {formError ? (
                    <p
                        className={styles.errorText}
                        role="alert"
                    >
                        {formError}
                    </p>
                ) : null}
                <form onSubmit={handleCreate}>
                    <p className={styles.muted}>
                        <strong>{'Rede'}</strong>
                    </p>
                    <div className={styles.formRow}>
                        <input
                            autoComplete="off"
                            className={styles.textInput}
                            id="platform-net-name"
                            maxLength={255}
                            placeholder="Nome da organização / rede"
                            type="text"
                            value={netName}
                            disabled={pending}
                            onChange={e => setNetName(e.target.value)}
                        />
                    </div>
                    <p className={styles.muted}>
                        <strong>{'Responsável (gestor da rede)'}</strong>
                    </p>
                    <div className={styles.formRow}>
                        <input
                            autoComplete="email"
                            className={styles.textInput}
                            placeholder="Email de login"
                            type="email"
                            value={adminEmail}
                            disabled={pending}
                            onChange={e => setAdminEmail(e.target.value)}
                        />
                        <input
                            autoComplete="name"
                            className={styles.textInput}
                            placeholder="Nome completo"
                            type="text"
                            value={adminFullName}
                            disabled={pending}
                            onChange={e => setAdminFullName(e.target.value)}
                        />
                    </div>
                    <div className={styles.formRow}>
                        <input
                            autoComplete="new-password"
                            className={styles.textInput}
                            maxLength={128}
                            minLength={8}
                            placeholder="Palavra-passe (mín. 8)"
                            type="password"
                            value={adminPassword}
                            disabled={pending}
                            onChange={e => setAdminPassword(e.target.value)}
                        />
                        <button
                            className={styles.btnPrimary}
                            disabled={pending}
                            type="submit"
                        >
                            {pending ? 'A criar…' : 'Criar rede'}
                        </button>
                    </div>
                </form>
            </div>
        </>
    );
};

export { PlatformNetworksView };
